import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Search, FileText, Plus, ChevronRight } from 'lucide-react';
import BottomNav from '../../components/BottomNav.tsx';
import { WorkStation } from '../../Interfaces/workstation.ts';
import { Building } from '../../Interfaces/building.ts';
import { processDocNJMH } from '../../utils/workInstructionVMF.ts';
import { ProcessDoc } from '../../Interfaces/processDocs.ts';
import ClientBottomNav from '../../components/ClientBottomNav.tsx';
import { baseurly } from '../../utils/apis.ts';
import { X_API_KEY } from '../../utils/apis.ts';
import {useAuth} from "../../context/authContext.tsx";
import MainLayout from '../../layouts/MainLayout.tsx';
import { digitalOceanImageUrl } from "../../utils/digitalOcean.ts";



const BuildingWorkstationsPage01: React.FC = () => {
  const {employeeI, authReady, addSignature} = useAuth()
  const { vehicleId, buildingId } = useParams();
  const navigate = useNavigate();

  const [processDoc, setProcessDoc] = useState<ProcessDoc | undefined>(undefined);
  const [building, setBuilding] = useState<Building | undefined>(undefined);
  const [workstations, setWorkstations] = useState<WorkStation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddProcessDoc, setShowAddProcessDoc] = useState<boolean>(false);
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    if (!authReady) {
      return;
    }
    if (!employeeI) {
      navigate('/login', { replace: true });
      return;
    }
    if (buildingId && vehicleId) {
      fetch(`${baseurly}/buildings/${buildingId}/work_stations`,{
              
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-API-KEY': X_API_KEY,
            },
            credentials: "include",
          })
        .then((res) => res.ok ? res.json() : Promise.reject('Failed to fetch workstations'))
        .then((data) => {
          setWorkstations(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching workstations:', err);
          setError('Unable to load workstations');
          setLoading(false);
        });

      fetch(`${baseurly}/building/${buildingId}`, {
              
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': X_API_KEY,
        },
        credentials: "include",
      })
        .then((res) => res.ok ? res.json() : Promise.reject('Failed to fetch building'))
        .then((data: Building) => {
          console.log(data);
          // setProcessDoc(processDocNJMH(data.process_docs, parseInt(buildingId), parseInt(vehicleId)));
          setBuilding(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching building:', err);
          setError('Unable to load building');
          setLoading(false);
        });
    }
  }, [authReady, buildingId, employeeI, navigate, vehicleId]);

  const handleWorkstationView = (workstationId: number) => {
    navigate(`/client/vehicle/${vehicleId}/building/${buildingId}/workstation/${workstationId}`);
  };

  const handleAddProcessDocClick = () => {
    setShowAddProcessDoc(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmitProcessDoc = async () => {
    if (!selectedBuildingId || !file || !vehicleId) {
      alert('Please select a building and upload a file.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      // Upload the file first
      const uploadResponse = await fetch(`${baseurly}/upload/processDoc`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': X_API_KEY,
        },
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error('File upload failed');
      }

      const filePath = await uploadResponse.text();

      // Submit the process document
      const payload = {
        building_id: parseInt(selectedBuildingId),
        vehicle_model_id: parseInt(vehicleId),
        file: filePath,
      };

      const submitResponse = await fetch(`${baseurly}/process_docs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!submitResponse.ok) {
        throw new Error('Failed to submit process document');
      }

      alert('Process document submitted successfully!');
      setShowAddProcessDoc(false);
      setSelectedBuildingId('');
      setFile(null);
    } catch (error) {
      console.error('Error submitting process document:', error);
      alert('Failed to submit process document');
    }
  };

  // Filter workstations based on the search query.
  const filteredWorkstations = workstations.filter((ws) =>
    ws.work_station.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <MainLayout>
      <div className="min-h-screen bg-blue-900 pb-20">
      {/* Header */}
      
      {/* Page Title */}
      <div className="p-6">
        <h1 className="text-white text-lg font-medium">Select a Workstation</h1>
      </div>

      {/* Search Bar */}
      <div className="px-6 mb-6">
        <div className="relative">
          <input 
            type="text"
            placeholder="Search workstations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full py-3 px-4 pr-10 rounded-full"
          />
          <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
      </div>

      {/* Workstations List Header */}
      <div className="bg-white ring-2 ring-white-500 rounded-t-3xl">
        <div className="px-6 mb-6 bg-white rounded-full p-6">
          <div className="bg-white rounded-xl border p-8 flex flex-col sm:flex-row justify-between items-center">
            <div>
              <h2 className="text-blue-900 text-xl font-bold mb-2">
                {building?.building_name} Work Stations
              </h2>
              <p className="text-gray-600 text-sm max-w-xs">
                Find clear, step-by-step instructions to help you complete your tasks efficiently and accurately.
              </p>
              {/* <button
                onClick={() => navigate(`/process-document/${processDoc?.id}`)}
                className="mt-4 bg-blue-900 text-white px-4 py-2 rounded-lg hover:bg-blue-800 flex items-center gap-2"
              >
                <FileText size={16} />
                Process Document
              </button> */}
            </div>
            <img
              src={digitalOceanImageUrl("products/kayoola_coach/images/orders-coach/carousel.png")}
              alt="Bus illustration"
              className="w-27 h-24"
            />
          </div>
        </div>

        {/* Add Process Document Modal */}
        {showAddProcessDoc && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
            <div className="bg-white p-6 rounded-lg w-96">
              <h2 className="text-blue-900 text-xl font-bold mb-4">Add Process Document</h2>
              <select
                value={selectedBuildingId}
                onChange={(e) => setSelectedBuildingId(e.target.value)}
                className="w-full p-2 border rounded mb-4"
              >
                <option value="">Select Building</option>
                {building && (
                  <option value={building.id}>{building.building_name}</option>
                )}
              </select>
              <input
                type="file"
                onChange={handleFileChange}
                className="w-full p-2 border rounded mb-4"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowAddProcessDoc(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitProcessDoc}
                  className="bg-blue-900 text-white px-4 py-2 rounded hover:bg-blue-800"
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Workstations List */}
        <div className="bg-white rounded-3xl p-6 mx-6">
          <h2 className="text-blue-900 text-xl font-bold mb-4">Workstations</h2>

          {loading ? (
            <p className="text-gray-600">Loading workstations...</p>
          ) : error ? (
            <p className="text-red-500">{error}</p>
          ) : filteredWorkstations.length > 0 ? (
            <ul className="space-y-3">
              {filteredWorkstations
              .sort((a, b) => a.id - b.id)
              .map((ws) => (
                <li
                  onClick={() => handleWorkstationView(ws.id)}
                  key={ws.id}
                  className="p-4 border bg-sky-200 font-semibold rounded-lg flex hover:bg-blue-200 justify-between items-center"
                >
                  {ws.work_station}
                  <button
                    className="px-4 py-2 text-blue-900 font-bold rounded hover:bg-gray-100"
                    onClick={() => handleWorkstationView(ws.id)}
                  >
                    <ChevronRight />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600">No workstations available.</p>
          )}
        </div>
      </div>

      {/* Bottom Navigation */}
      <ClientBottomNav showBack={true} />
    </div>
    </MainLayout>
    
  );
};

export default BuildingWorkstationsPage01;
